import React, {useEffect, useState, useRef, useCallback, useMemo} from 'react';

import {useStore} from 'effector-react';
import {i18n as i18nType} from 'i18next';
import {FileRejection, useDropzone} from 'react-dropzone';
import {useTranslation} from 'react-i18next';
import {AnySchema, ValidationError} from 'yup';

import {fetchProductCardTemplate} from 'api';
import {ReactComponent as AlcoholIcon} from 'assets/svg/alcohol.svg';
import {ReactComponent as BiofuelsIcon} from 'assets/svg/biofuels.svg';
import {ReactComponent as CoffeeIcon} from 'assets/svg/coffee.svg';
import {ReactComponent as CrossIcon} from 'assets/svg/cross.svg';
import {ReactComponent as UploadIcon} from 'assets/svg/download.svg';
import {ReactComponent as DrinkIcon} from 'assets/svg/drink.svg';
import {ReactComponent as DrugIcon} from 'assets/svg/drug.svg';
import {ReactComponent as FuelIcon} from 'assets/svg/fuel.svg';
import {ReactComponent as TobaccoIcon} from 'assets/svg/tobacco.svg';
import {ReactComponent as VetIcon} from 'assets/svg/vet.svg';
import {getTemplate} from 'components/pages/Registries/mock';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {IconButton} from 'components/ui-kit/IconButton';
import {Spinner} from 'components/ui-kit/Spinner';
import {apiClient} from 'core/apiClient';
import {ProductGroups} from 'core/constants/productGroups';
import {profileCountries$} from 'models/profile/common';
import {packagingFormatTypeDictionary$} from 'models/registries/gtinsGrid/table';
import {getHsnCodesFx, productGroups$} from 'models/registries/gtinsGrid/upload';
import {colors} from 'theme';

import {Country, PackagingFormatType, ProductStatuses} from 'types/gtins';
import {readXlsxProducts} from 'utils/readXlsxProducts';

import {handleValuesFromExternalDictionary, handleValuesFromInternalDictionary} from './helpers';
import {
    ErrorList,
    ErrorItem,
    Root,
    ModalContent,
    RootContent,
    HighlightedText,
    FileExample,
    FileExampleList,
    FileExampleItem,
    FileExampleTitle,
    FileRejectionErrorList,
    FileRejectionErrorMessage,
    FileNameContainer,
    FileName,
    ErrorListContaienr,
    FileExampleListLoading,
} from './styled';

interface MapObject {
    [key: string]: string | boolean | number;
}

export interface FileFormPropsCallback {
    file?: File | null;
    data: Array<MapObject>;
    errors?: Array<string>;
    isValid?: boolean;
}
export interface FileFormProps {
    onChange(data: FileFormPropsCallback): void;
    scheme?: AnySchema;
    initialValue?: File | null;
    errors?: XLSXError[];
    serverValidate?: boolean;
}

export type XLSXError = {field: string; error: string};

type AttributeEnumOption = {
    code: string;
    name: string;
};

type AttributeConfig = {
    code?: string;
    fieldName?: string;
    dataType?: string;
    enumValues?: AttributeEnumOption[];
};

type AttributeSchemaLookup = {
    booleanFields: Set<string>;
    enumFields: Map<string, Map<string, string>>;
};

const attributeSchemaCache: Partial<Record<string, Promise<AttributeSchemaLookup>>> = {};

const toCustomerLanguage = (language: string) => (language.startsWith('fr') ? 'fr' : 'en');

const normalizeLookupValue = (value: unknown) =>
    String(value ?? '')
        .trim()
        .replace(/\s+/g, ' ')
        .toLowerCase();

const getAttributeSchemaLookup = (
    language: string,
    params: {productGroupCode?: string; productCategory?: string},
) => {
    const customerLanguage = toCustomerLanguage(language);
    const cacheKey = [
        customerLanguage,
        params.productGroupCode ?? '',
        params.productCategory ?? '',
    ].join('|');

    if (!attributeSchemaCache[cacheKey]) {
        attributeSchemaCache[cacheKey] = apiClient
            .get<AttributeConfig[]>('service/attribute-config/schema/product-cards', {
                params,
                headers: {'Customer-Language': customerLanguage},
            })
            .then(({data}) =>
                data.reduce<AttributeSchemaLookup>(
                    (acc, field) => {
                        const fieldName = field.fieldName ?? field.code;
                        if (!fieldName) return acc;

                        if (field.dataType?.toUpperCase() === 'BOOLEAN') {
                            acc.booleanFields.add(fieldName);
                        }

                        if (field.enumValues?.length) {
                            const enumValues = acc.enumFields.get(fieldName) ?? new Map();

                            field.enumValues.forEach((option) => {
                                enumValues.set(normalizeLookupValue(option.code), option.code);
                                enumValues.set(normalizeLookupValue(option.name), option.code);
                            });

                            acc.enumFields.set(fieldName, enumValues);
                        }

                        return acc;
                    },
                    {
                        booleanFields: new Set<string>(),
                        enumFields: new Map<string, Map<string, string>>(),
                    },
                ),
            );
    }

    return attributeSchemaCache[cacheKey]!;
};

const normalizeXlsxValueBySchema = (
    key: string,
    value: MapObject[string],
    schemaLookup: AttributeSchemaLookup,
) => {
    const normalizedValue = normalizeLookupValue(value);

    if (schemaLookup.booleanFields.has(key)) {
        if (normalizedValue === 'yes') return true;
        if (normalizedValue === 'no') return false;
    }

    const enumCode = schemaLookup.enumFields.get(key)?.get(normalizedValue);
    return enumCode || value;
};

const normalizeXlsxRowsBySchema = (
    rows: Record<string, MapObject[string]>[],
    schemaLookup: AttributeSchemaLookup,
) =>
    rows.map((row) =>
        Object.entries(row).reduce<Record<string, MapObject[string]>>((acc, [key, value]) => {
            acc[key] = normalizeXlsxValueBySchema(key, value, schemaLookup);
            return acc;
        }, {}),
    );

const normalizeXlsxRowsByProductSchema = async (
    rows: Record<string, MapObject[string]>[],
    language: string,
) => {
    const rowsByProductGroup = rows.reduce<Record<string, Record<string, MapObject[string]>[]>>(
        (acc, row) => {
            const productGroupCode = String(row.product_group_code ?? '');
            if (!productGroupCode) return acc;

            acc[productGroupCode] = [...(acc[productGroupCode] ?? []), row];
            return acc;
        },
        {},
    );

    const normalizedRows = await Promise.all(
        Object.entries(rowsByProductGroup).flatMap(async ([productGroupCode, groupedRows]) => {
            const productGroupSchema = await getAttributeSchemaLookup(language, {
                productGroupCode,
            });
            const rowsWithProductCategory = normalizeXlsxRowsBySchema(
                groupedRows,
                productGroupSchema,
            );

            return Promise.all(
                rowsWithProductCategory.map(async (row) => {
                    const productCategory = String(row.product_category ?? '');
                    const productSchema = await getAttributeSchemaLookup(language, {
                        productGroupCode,
                        productCategory: productCategory || undefined,
                    });

                    return normalizeXlsxRowsBySchema([row], productSchema)[0];
                }),
            );
        }),
    );

    return normalizedRows.flat();
};

const handleLanguageDiff = (
    key: string,
    item: string,
    i18n: i18nType,
    packagingFormatTypeDictionary: PackagingFormatType[],
    profileCountries: Country[],
) => {
    const TYPE_TO_GET_FROM_EXTERNAL_DICTIONARY: Record<string, any> = {
        packaging_type: packagingFormatTypeDictionary,
        country_manufacture: profileCountries,
    };
    const dictionariesFields: Record<string, string> = {
        package_type: 'filePackageTypes',
        product_group_code: 'productGroups',
    };

    if (TYPE_TO_GET_FROM_EXTERNAL_DICTIONARY[key]) {
        return handleValuesFromExternalDictionary(
            key,
            item,
            i18n,
            packagingFormatTypeDictionary,
            profileCountries,
        );
    }

    if (dictionariesFields[key]) {
        return handleValuesFromInternalDictionary(key, item, i18n, dictionariesFields);
    }

    return item;
};

const getHsnCodes = (array: Record<string, string>[]) =>
    array.reduce((acc, item) => {
        if (acc[item.hsn_code]) {
            return acc;
        }

        return {...acc, [item.hsn_code]: item.hsn_code};
    }, {});

const mapTemplateField = (field: string) => (field === 'gtin' ? 'product_code' : field);

export const mapByTemplate = async (
    array: Array<string[]>,
    file_type: string,
    i18n: i18nType,
    packagingFormatTypeDictionary: PackagingFormatType[],
    profileCountries: Country[],
) => {
    const file_type_translated = handleLanguageDiff(
        'product_group_code',
        file_type,
        i18n,
        packagingFormatTypeDictionary,
        profileCountries,
    );
    const template = getTemplate(file_type_translated);

    const res = array.map((arrItem) =>
        arrItem.reduce(
            (acc, item, index) => {
                const templateField = template[index];
                const field = mapTemplateField(templateField);

                if (item.toString() === '' && field !== 'product_code') {
                    return acc;
                }
                Object.assign(acc, {
                    [field]: handleLanguageDiff(
                        field,
                        item,
                        i18n,
                        packagingFormatTypeDictionary,
                        profileCountries,
                    ),
                });
                return acc;
            },
            ({
                product_group_code: file_type_translated,
                product_code: '',
                status: ProductStatuses.PENDING,
            } as unknown) as Record<string, string>,
        ),
    );

    const hsnCodes = getHsnCodes((res as unknown) as Record<string, string>[]);

    try {
        const hsnCodesData = await getHsnCodesFx(Object.keys(hsnCodes));

        const rows = res.map((item) => ({
            ...item,
            hsn_description:
                hsnCodesData.find((hsn) => hsn.code === item.hsn_code)?.description || '',
        }));

        return normalizeXlsxRowsByProductSchema(rows, i18n.language);
    } catch (error) {
        console.error(error);
    }
};

export const XlsxMultipleFilesForm = ({
    onChange,
    scheme,
    initialValue,
    errors: errorsList,
    serverValidate,
}: FileFormProps) => {
    const {t, i18n} = useTranslation();
    const didMountRef = useRef(false);
    const [file, fileChanged] = useState<File | null>(initialValue || null);
    const [errors, errorsChanged] = useState<Array<string> | null>(null);
    const [data, dataChanged] = useState<Array<MapObject>>([]);
    const [fileRejectionMessages, setFileRejectionMessages] = useState<Array<string>>([]);
    const DRAGNDROP_LOCATION = 'pages.documents.invoices.create.invoiceLines.xlsxModal';
    const packagingFormatTypeDictionary = useStore(packagingFormatTypeDictionary$);
    const profileCountries = useStore(profileCountries$);
    const productGroups = useStore(productGroups$);

    const handleChangeFile = (droppedFile: File | null): void => {
        if (!droppedFile) {
            fileChanged(null);
            dataChanged([]);
            return;
        }

        if (serverValidate) {
            fileChanged(droppedFile);
            dataChanged([]);
            return;
        }

        const {file} = readXlsxProducts(
            droppedFile,
            async (data: any, _: File, file_type: string) => {
                const mappedData = await mapByTemplate(
                    data,
                    file_type,
                    i18n,
                    packagingFormatTypeDictionary,
                    profileCountries,
                );

                dataChanged(mappedData || []);
            },
        );

        if (file) {
            fileChanged(file);
        } else {
            fileChanged(null);
            dataChanged([]);
        }
    };

    useEffect(() => {
        if (!serverValidate && scheme && file && didMountRef.current) {
            scheme
                .validate(data, {abortEarly: false})
                .then(() => {
                    errorsChanged([]);
                })
                .catch((err) => {
                    const errors: Array<string> = [];
                    err.inner.forEach((e: ValidationError) => {
                        const path = e.path ? e.path.match(/\[(.*)\]/) ?? [] : [];
                        const stringCount = path.pop();
                        errors.push(
                            stringCount
                                ? `${t('components.xlsxForm.row')}: ${
                                      stringCount ? parseInt(stringCount, 10) + 2 : '-'
                                  }: ` + (i18n.exists(e.message) ? t(e.message) : e.message)
                                : e.message,
                        );
                    });
                    errorsChanged(errors);
                });
        } else if (didMountRef.current && !file) {
            errorsChanged(null);
        }
    }, [data]);

    useEffect(() => {
        if (!file && didMountRef.current) {
            dataChanged([]);
        }
    }, [file]);

    useEffect(() => {
        if (didMountRef.current) {
            onChange({
                file,
                data,
                errors: errors === null ? [] : errors,
                isValid: errors !== null && !errors.length,
            });
        } else {
            didMountRef.current = true;
        }
    }, [file, data, errors]);

    const onDrop = useCallback(
        (fl: File[], fileRejections: FileRejection[]) => {
            setFileRejectionMessages([]);
            if (fileRejections.length > 0) {
                setFileRejectionMessages(() =>
                    fileRejections[0].errors.map((error) => error.message),
                );
            }
            if (fl.length > 0) {
                const selectedFile = fl[0];
                handleChangeFile(selectedFile);
            }
        },
        [file, handleChangeFile],
    );
    const {getRootProps, getInputProps, isDragActive} = useDropzone({onDrop, accept: '.xlsx,.xls'});

    function setFileIcons(code: keyof typeof ProductGroups) {
        switch (code) {
            case ProductGroups.TOBACCO:
                return TobaccoIcon;
            case ProductGroups.DRINKS:
                return DrinkIcon;
            case ProductGroups.ALCOHOL:
                return AlcoholIcon;
            case ProductGroups.COFFEE:
                return CoffeeIcon;
            case ProductGroups.MEDICATIONS:
                return DrugIcon;
            case ProductGroups.VETERINARY:
                return VetIcon;
            case ProductGroups.FUEL:
                return FuelIcon;
            case ProductGroups.BIOFUELS:
                return BiofuelsIcon;
            default:
                return TobaccoIcon;
        }
    }

    const fileExamples = useMemo(
        () =>
            productGroups?.map((group) => ({
                title: group.name,
                code: group.code,
                icon: setFileIcons(group.code),
            })),
        [i18n.language, productGroups],
    );

    const [downloadPending, setDownloadPending] = useState(false);

    async function downloadTemplate(code: keyof typeof ProductGroups) {
        setDownloadPending(true);
        const template = await fetchProductCardTemplate(code);
        setDownloadPending(false);

        const blob = new Blob([template], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;

        a.download = `${code}_TEMPLATE.xlsx`;
        document.body.appendChild(a);
        a.click();

        URL.revokeObjectURL(url);
        document.body.removeChild(a);
        a.remove();
    }

    return (
        <>
            <ModalContent>
                <FileExample>
                    <FileExampleTitle>{t('components.xlsxForm.needHelp')}</FileExampleTitle>
                    <FileExampleList>
                        {downloadPending && (
                            <FileExampleListLoading>
                                <Spinner size={1} />
                            </FileExampleListLoading>
                        )}
                        {fileExamples?.map((example) => (
                            <FileExampleItem key={example.title}>
                                <Button
                                    disabled={downloadPending}
                                    style={{
                                        padding: 0,
                                        whiteSpace: 'nowrap',
                                    }}
                                    type="button"
                                    size="sm"
                                    variant={ButtonVariant.Text}
                                    icon={
                                        <Icon
                                            style={{
                                                padding: '6px',
                                                backgroundColor: colors.brandLight,
                                                borderRadius: '4px',
                                            }}
                                            source={example.icon}
                                            width={16}
                                            height={16}
                                        />
                                    }
                                    onClick={() => downloadTemplate(example.code)}
                                >
                                    {t('components.xlsxForm.downloadTemplate')}
                                </Button>
                            </FileExampleItem>
                        ))}
                    </FileExampleList>
                </FileExample>
                {file ? (
                    <FileNameContainer>
                        <FileName>{file.name}</FileName>
                        <IconButton
                            icon={
                                <Icon
                                    source={CrossIcon}
                                    width={11}
                                    height={11}
                                    fillColor={colors.textPrimary}
                                />
                            }
                            onClick={() => handleChangeFile(null)}
                            type="button"
                        />
                    </FileNameContainer>
                ) : (
                    <Root isDrag={isDragActive} {...getRootProps()}>
                        <input {...getInputProps()} />
                        {isDragActive ? (
                            <p>{t('pages.dashboard.dragHere')}</p>
                        ) : (
                            <RootContent>
                                <Icon source={UploadIcon} width={16} height={16} />
                                <p>
                                    <HighlightedText>
                                        {t(`${DRAGNDROP_LOCATION}.dragAndDrop`)}
                                    </HighlightedText>{' '}
                                    {t(`${DRAGNDROP_LOCATION}.aFileHere`)}{' '}
                                    <HighlightedText>
                                        {t(`${DRAGNDROP_LOCATION}.chooseFile`)}
                                    </HighlightedText>
                                </p>
                            </RootContent>
                        )}
                    </Root>
                )}
                {fileRejectionMessages.length > 0 && (
                    <FileRejectionErrorList>
                        {fileRejectionMessages.map((message, i) => (
                            <FileRejectionErrorMessage key={i}>{message}</FileRejectionErrorMessage>
                        ))}
                    </FileRejectionErrorList>
                )}
            </ModalContent>
            {serverValidate && errorsList && errorsList.length > 0 && (
                <ErrorListContaienr>
                    <ErrorList>
                        {errorsList?.map(({field, error}, i) => (
                            <ErrorItem key={i}>
                                Field <b>{field}:</b> {error}
                            </ErrorItem>
                        ))}
                    </ErrorList>
                </ErrorListContaienr>
            )}
        </>
    );
};
